/**
 * Copy a work order into a NEW quote (#3702).
 *
 * `POST /work-orders/:id/copy`. The source work order is not changed, so its
 * own detail cache is left alone. What a new quote does change: the work
 * order lists and counters (the quote queue is one of the lists), the calendar
 * and booking lists, and, only when a new mileage reading was entered, the
 * vehicle's mileage.
 *
 * Query keys carry no tenant: the cache belongs to the signed-in workshop's
 * session and is dropped when the session ends, so these invalidations reach
 * exactly that tenant's data.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export type CopyWorkOrderMode = 'all' | 'unfinished';

export interface CopyWorkOrderBody {
  mode: CopyWorkOrderMode;
  /** ISO instant, required: the new quote never inherits the old schedule. */
  scheduledAt: string;
  customerRequestedPickupAt?: string;
  vehicleDropoffAt?: string;
  /** A NEW odometer reading. Left out, the vehicle's mileage is not touched. */
  mileage?: number;
}

export interface CopyWorkOrderSummary {
  servicesCopied: number;
  servicesSkippedFinished: number;
  laborItemsCopied: number;
  laborItemsSkippedCompleted: number;
  itemsCopied: number;
  itemsSkippedCompleted: number;
  itemsSkippedRefused: number;
  stockPartsCopiedAsItems: number;
  stockPartsSkippedFinished: number;
  stockPartsSkippedRefused: number;
  fixedPriceServicesPartlyDone: number;
}

export interface CopyWorkOrderResult {
  id: string;
  workOrderNumber: string | null;
  status: 'quote';
  sourceId: string;
  mode: CopyWorkOrderMode;
  scheduledAt: string;
  summary: CopyWorkOrderSummary;
}

export interface CopyWorkOrderVariables {
  body: CopyWorkOrderBody;
  /** Sent as Idempotency-Key: a retried or double-sent request makes one quote. */
  idempotencyKey: string;
}

export function useCopyWorkOrder(sourceId: string, vehicleId?: string) {
  const queryClient = useQueryClient();

  return useMutation<CopyWorkOrderResult, Error, CopyWorkOrderVariables>({
    mutationFn: ({ body, idempotencyKey }) =>
      api.post<CopyWorkOrderResult>(`/work-orders/${sourceId}/copy`, body, {
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
    onSuccess: (_result, { body }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.stats() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.all,
        predicate: (query) =>
          query.queryKey[0] === 'bookings' && (query.queryKey[1] === 'list' || query.queryKey[1] === 'calendar'),
      });
      if (body.mileage !== undefined && vehicleId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.detail(vehicleId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.lists() });
      }
    },
  });
}

/**
 * What a failed copy request tells us about whether a quote was made.
 *
 * - `rejected`: the server refused it before or instead of creating anything
 *   (validation, permission, missing source, a known "nothing to copy"
 *   answer). The Idempotency-Key can go; the user corrects and sends anew.
 * - `retryable`: no answer we can read (network, 5xx, session or rate limit).
 *   A quote may exist. The SAME key and body are sent again; the server
 *   either replays the original answer or refuses, it never makes a second.
 * - `in_progress`: the server says the original is still running. Same
 *   treatment as `retryable`, with a different message.
 * - `unresolved`: the server says the original's fate is undetermined, or
 *   the key no longer matches. Sending again cannot settle it, and a new key
 *   could make a second quote, so only an explicit user choice moves on.
 * - `blocked`: a guard or validation refusal (400, 403, 404, a known "nothing
 *   to copy" answer) to a REPLAY of an attempt whose outcome is unknown. Such a
 *   refusal proves only that this replay did not run, not that the original
 *   send did not commit (a lost response, then lost permission or a moved
 *   route). The key is kept: the same key and body may be sent again once the
 *   cause is fixed, or the user explicitly starts a new copy.
 */
export type CopyFailureKind = 'rejected' | 'retryable' | 'in_progress' | 'unresolved' | 'blocked';

export interface ClassifyCopyFailureOptions {
  /**
   * The failed send replayed an attempt that was already unsettled (earlier
   * send lost, still in progress, or its dialog went away mid-flight). A
   * `rejected` answer then becomes `blocked`: it cannot release the key.
   */
  priorOutcomeUnknown?: boolean;
}

/** "Nothing to copy" and "source is gone" answers the copy handler gives before it writes anything. */
const DEFINITIVE_CONFLICT_CODES = new Set([
  'no_unfinished_work',
  'no_work_to_copy',
  'source_orderer_unavailable',
  'source_vehicle_unavailable',
]);

/** Status codes that come from validation or route guards, ahead of any write. */
const DEFINITIVE_STATUSES = new Set([400, 403, 404]);

/** Pre-handler refusals where nothing ran but the same request may be sent again. */
const RETRYABLE_CLIENT_STATUSES = new Set([401, 423, 429]);

/**
 * The server's body token. The idempotency plugin answers `{ error: 'IDEMPOTENCY_...' }`;
 * the copy route answers `{ code: '...' }`. Duck-typed: tests mock `@/lib/api`.
 */
function bodyTokens(error: unknown): string[] {
  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return [];
  const { error: errorField, code } = data as { error?: unknown; code?: unknown };
  return [errorField, code].filter((value): value is string => typeof value === 'string' && value !== '');
}

export function classifyCopyFailure(error: unknown, options: ClassifyCopyFailureOptions = {}): CopyFailureKind {
  const kind = classifyFreshFailure(error);
  return kind === 'rejected' && options.priorOutcomeUnknown ? 'blocked' : kind;
}

function classifyFreshFailure(error: unknown): Exclude<CopyFailureKind, 'blocked'> {
  const status = error instanceof Error ? (error as { status?: unknown }).status : undefined;
  // fetch rejects with a TypeError (or aborts): the request may have reached the server.
  if (typeof status !== 'number') return 'retryable';

  const tokens = bodyTokens(error);
  if (tokens.includes('IDEMPOTENCY_IN_PROGRESS')) return 'in_progress';
  if (tokens.some((token) => token.startsWith('IDEMPOTENCY_'))) return 'unresolved';

  if (status >= 500 || RETRYABLE_CLIENT_STATUSES.has(status)) return 'retryable';
  if (DEFINITIVE_STATUSES.has(status)) return 'rejected';
  if (status === 409 && tokens.some((token) => DEFINITIVE_CONFLICT_CODES.has(token))) return 'rejected';
  // Any other 4xx (an unknown 409, 422, ...) is not proof that nothing was created.
  return 'unresolved';
}

/** The values as the user typed them, so a locked form can show them again. */
export interface CopyFormSnapshot {
  mode: CopyWorkOrderMode;
  scheduledAt: string;
  pickupAt: string;
  dropoffAt: string;
  mileage: string;
}

/** A request that was sent and is not settled: the exact key and body, never rebuilt. */
export interface CopyAttempt {
  idempotencyKey: string;
  body: CopyWorkOrderBody;
  form: CopyFormSnapshot;
  /** `sending` = the dialog went away before an answer arrived; handled like `retryable`. */
  phase: 'sending' | 'retryable' | 'in_progress' | 'unresolved' | 'blocked';
}

/** Who the unsettled attempts belong to. Both parts are part of the map key. */
export interface CopyAttemptScope {
  tenantId: string | null | undefined;
  userId: string | null | undefined;
}

/**
 * Unsettled attempts, in this tab's memory only, per tenant, signed-in user and
 * source work order. The dialog is mounted only while open, so state kept
 * inside it would be lost on close and the next submit would get a new key.
 * Dates, mode and mileage are all it holds; no customer data.
 *
 * Lifetime: the module's memory, until the page is reloaded. Signing out does
 * NOT clear it (nothing here is wired to the auth store); `clearCopyAttempts`
 * is only called by tests. The scope is what keeps it from leaking: another
 * tenant or another user in the same tab never sees the entry, while the same
 * user signing back in does, and replays the same key (which the server ties to
 * that principal). After a reload the user must check the quote list themselves.
 */
const unsettledAttempts = new Map<string, CopyAttempt>();

// JSON keeps ids that contain a separator from colliding across the three parts.
const attemptScope = (scope: CopyAttemptScope, sourceId: string) =>
  JSON.stringify([scope.tenantId ?? '', scope.userId ?? '', sourceId]);

export function getCopyAttempt(scope: CopyAttemptScope, sourceId: string): CopyAttempt | null {
  return unsettledAttempts.get(attemptScope(scope, sourceId)) ?? null;
}

export function saveCopyAttempt(scope: CopyAttemptScope, sourceId: string, attempt: CopyAttempt): void {
  unsettledAttempts.set(attemptScope(scope, sourceId), attempt);
}

export function discardCopyAttempt(scope: CopyAttemptScope, sourceId: string): void {
  unsettledAttempts.delete(attemptScope(scope, sourceId));
}

/** Drops every unsettled attempt. Called by tests only; sign-out does not call it. */
export function clearCopyAttempts(): void {
  unsettledAttempts.clear();
}
